import {FC, useEffect, useMemo, useState} from 'react'
import {AzPageHeader} from '../../../components/AzPageHeader'
import {useIntl} from 'react-intl'
import {useQuery} from 'react-query'
import {getTourReservations} from '../../../services/tourReservations'
import {KTSVG} from '../../../../_dashboard/helpers'
import {useNavigate} from 'react-router-dom'
import {useRef} from 'react'
import {Tooltip, OverlayTrigger} from 'react-bootstrap'
import {toast} from 'react-toastify'

const getEmptyDateRange = () => ({
  dateFrom: '',
  dateTo: '',
})

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  return debouncedValue
}

const normalizeReservationStatusCode = (rawCode?: string) => {
  const normalized = (rawCode || '').trim().toLowerCase()
  if (!normalized) return 'pending'
  return normalized.startsWith('status_') ? normalized.replace(/^status_/, '') : normalized
}

type SortField = 'orderNumber' | 'tourDate' | 'totalPrice' | 'createdAt' | 'adultsCount'

type TourReservationsFilters = {
  page: number
  limit: number
  status?: string
  dateFrom: string
  dateTo: string
  createdFrom: string
  createdTo: string
  search: string
  sortBy?: SortField
  sortDirection?: 'asc' | 'desc'
}

const getClientName = (reservation: any) => {
  const participant = reservation?.participants?.[0]
  if (participant) {
    const name = [participant.firstName, participant.lastName].filter(Boolean).join(' ')
    if (name) return name
  }
  return '-'
}

const buildExportRows = (reservations: any[], intl: any) => {
  const pass = '-'
  return reservations.map((r) => {
    const statusCode = normalizeReservationStatusCode(r?.status?.code)
    return {
      'ORDER NUMBER': r?.orderNumber ?? pass,
      'CREATED AT': r?.createdAt ? new Date(r.createdAt).toLocaleString() : pass,
      'TOUR DATE': r?.tourDate ? new Date(r.tourDate).toLocaleDateString() : pass,
      TOUR: r?.tour?.title ?? pass,
      CLIENT: getClientName(r),
      ADULTS: typeof r?.adultsCount === 'number' ? r.adultsCount : pass,
      CHILDREN: typeof r?.childrenCount === 'number' ? r.childrenCount : pass,
      'TOTAL PRICE': typeof r?.totalPrice === 'number' ? Number(r.totalPrice).toFixed(2) : pass,
      CURRENCY: r?.currencySymbol ?? pass,
      STATUS: intl.formatMessage({id: `STATUS.${String(statusCode).toUpperCase()}`}) ?? statusCode,
      'PAYMENT METHOD':
        r?.paymentMethod?.description || r?.paymentMethod?.code || r?.paymentMethod || pass,
    }
  })
}

const TourReservationsPage: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {dateFrom, dateTo} = getEmptyDateRange()
  const [filters, setFilters] = useState<TourReservationsFilters>({
    page: 0,
    limit: 10,
    status: undefined,
    dateFrom,
    dateTo,
    createdFrom: '',
    createdTo: '',
    search: '',
    sortBy: 'createdAt',
    sortDirection: 'desc',
  })
  const [exporting, setExporting] = useState(false)

  const debouncedSearch = useDebounce(filters.search, 500)
  const debouncedFilters = {...filters, search: debouncedSearch}

  const toggleSort = (field: SortField) => {
    setFilters((prev) => {
      if (prev.sortBy !== field) {
        return {...prev, sortBy: field, sortDirection: 'asc', page: 0}
      }
      if (prev.sortDirection === 'asc') {
        return {...prev, sortDirection: 'desc', page: 0}
      }
      return {...prev, sortBy: 'createdAt', sortDirection: 'desc', page: 0}
    })
  }

  const sortIndicator = (field: SortField) => {
    if (filters.sortBy !== field) return ''
    return filters.sortDirection === 'asc' ? ' ↑' : ' ↓'
  }

  const {data, isLoading, error} = useQuery(
    ['tourReservations', debouncedFilters],
    () => getTourReservations(debouncedFilters),
    {
      keepPreviousData: true,
    }
  )

  const reservations = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data])

  useEffect(() => {
    // Ensure dateFrom <= dateTo so filtering/export always behaves predictably
    if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
      setFilters((prev) => ({...prev, dateTo: prev.dateFrom}))
    }
    if (filters.createdFrom && filters.createdTo && filters.createdFrom > filters.createdTo) {
      setFilters((prev) => ({...prev, createdTo: prev.createdFrom}))
    }
  }, [filters.dateFrom, filters.dateTo, filters.createdFrom, filters.createdTo])

  const exportExcel = async () => {
    try {
      setExporting(true)

      const all: any[] = []
      const base: TourReservationsFilters = {
        page: 0,
        limit: 500,
        status: filters.status,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        createdFrom: filters.createdFrom,
        createdTo: filters.createdTo,
        // export should match what user typed (not wait for debounce)
        search: filters.search,
      }

      for (let page = 0; page < 500; page++) {
        const res = await getTourReservations({...base, page})
        const chunk = Array.isArray(res?.data) ? res.data : []
        all.push(...chunk)
        if (chunk.length < base.limit) break
      }

      if (all.length === 0) {
        toast.info(
          intl.formatMessage({
            id: 'TOUR_RESERVATIONS.EXPORT_EMPTY',
            defaultMessage: 'No reservations to export for this period.',
          }),
        )
        return
      }

      const {utils, writeFile} = await import('xlsx')
      const rows = buildExportRows(all, intl)
      const ws = utils.json_to_sheet(rows)
      const wb = utils.book_new()
      utils.book_append_sheet(wb, ws, 'Tour reservations')

      const safeFrom = (filters.dateFrom || '').replaceAll('-', '')
      const safeTo = (filters.dateTo || '').replaceAll('-', '')
      const fileName = `tour-reservations_${safeFrom || 'from'}_${safeTo || 'to'}.xlsx`
      writeFile(wb, fileName)

      toast.success(
        intl.formatMessage({
          id: 'TOUR_RESERVATIONS.EXPORT_OK',
          defaultMessage: 'Excel exported successfully.',
        }),
      )
    } catch (e) {
      console.error(e)
      toast.error(
        intl.formatMessage({
          id: 'TOUR_RESERVATIONS.EXPORT_ERR',
          defaultMessage: 'Export failed. Please try again.',
        }),
      )
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <AzPageHeader title={intl.formatMessage({id: 'TOUR_RESERVATIONS.TITLE'})} />

      <div className='card'>
        <div className='card-header border-0 pt-6 flex-wrap gap-3'>
          <div className='card-title w-100 w-md-auto m-0'>
            <div className='bo-filters d-flex flex-wrap align-items-center position-relative my-1 gap-2 w-100'>
              <KTSVG
                path='/media/icons/duotune/general/gen021.svg'
                className='svg-icon-1 position-absolute ms-6'
              />
              <input
                type='text'
                className='form-control form-control-solid w-100 w-md-250px ps-14'
                placeholder={intl.formatMessage({id: 'TOUR_RESERVATIONS.SEARCH'})}
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value, page: 0})}
              />
              <input
                type='date'
                className='form-control flex-grow-1'
                value={filters.dateFrom}
                onChange={(e) => {
                  setFilters({...filters, dateFrom: e.target.value, page: 0})
                }}
                style={{minWidth: 140, maxWidth: 180}}
              />
              <span>to</span>
              <input
                type='date'
                className='form-control flex-grow-1'
                value={filters.dateTo}
                onChange={(e) => {
                  setFilters({...filters, dateTo: e.target.value, page: 0})
                }}
                style={{minWidth: 140, maxWidth: 180}}
              />

              <span className='text-muted fs-7 text-nowrap'>Created</span>
              <input
                type='date'
                className='form-control flex-grow-1'
                value={filters.createdFrom}
                onChange={(e) => {
                  setFilters({...filters, createdFrom: e.target.value, page: 0})
                }}
                style={{minWidth: 140, maxWidth: 180}}
              />
              <span>to</span>
              <input
                type='date'
                className='form-control flex-grow-1'
                value={filters.createdTo}
                onChange={(e) => {
                  setFilters({...filters, createdTo: e.target.value, page: 0})
                }}
                style={{minWidth: 140, maxWidth: 180}}
              />
            </div>
          </div>
          <div className='card-toolbar w-100 w-md-auto m-0'>
            <div className='d-flex flex-wrap justify-content-start justify-content-md-end gap-2 w-100'>
              <button
                type='button'
                className='btn btn-light-primary btn-sm'
                disabled={exporting}
                onClick={exportExcel}
              >
                <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-3 me-1' />
                {exporting
                  ? '...'
                  : intl.formatMessage({
                      id: 'TOUR_RESERVATIONS.EXPORT_EXCEL',
                      defaultMessage: 'Export Excel',
                    })}
              </button>
              <select
                className='form-select form-select-solid flex-grow-1'
                style={{minWidth: 160, maxWidth: 240}}
                value={filters.status || ''}
                onChange={(e) => setFilters({...filters, status: e.target.value || undefined, page: 0})}
              >
                <option value=''>{intl.formatMessage({id: 'STATUS.ALL', defaultMessage: 'All Statuses'})}</option>
                <option value='pending'>{intl.formatMessage({id: 'STATUS.PENDING'})}</option>
                <option value='confirmed'>{intl.formatMessage({id: 'STATUS.CONFIRMED'})}</option>
                <option value='canceled'>{intl.formatMessage({id: 'STATUS.CANCELED'})}</option>
                <option value='completed'>{intl.formatMessage({id: 'STATUS.COMPLETED'})}</option>
                <option value='failed'>{intl.formatMessage({id: 'STATUS.FAILED'})}</option>
              </select>
            </div>
          </div>
        </div>

        <div className='card-body py-4'>
          {isLoading ? (
            <div className='d-flex justify-content-center'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className='alert alert-danger' role='alert'>
              {intl.formatMessage({id: 'ERROR.LOADING_DATA'})}
            </div>
          ) : (
            (() => {
              if (reservations.length === 0) {
                return (
                  <div className='d-flex flex-column align-items-center justify-content-center py-10'>
                    <KTSVG
                      path='/media/icons/duotune/general/gen004.svg'
                      className='svg-icon-3x text-muted mb-5'
                    />
                    <div className='text-center'>
                      <h3 className='text-gray-900 fw-bold mb-2'>
                        {intl.formatMessage({
                          id: 'TOUR_RESERVATIONS.NO_RESERVATIONS',
                          defaultMessage: 'No Reservations Found'
                        })}
                      </h3>
                      <p className='text-gray-600 fs-6'>
                        {intl.formatMessage({
                          id: 'TOUR_RESERVATIONS.NO_RESERVATIONS_DESC',
                          defaultMessage: 'No tour reservations match your current filters. Try adjusting your search criteria or date range.'
                        })}
                      </p>
                    </div>
                  </div>
                )
              }
              
              return (
                <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-5'>
                  <thead>
                    <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                      <th
                        role='button'
                        style={{cursor: 'pointer'}}
                        onClick={() => toggleSort('orderNumber')}
                      >
                        {intl.formatMessage({id: 'TOUR_RESERVATIONS.ORDER_NUMBER'})}
                        {sortIndicator('orderNumber')}
                      </th>
                      <th>{intl.formatMessage({id: 'TOUR_RESERVATIONS.TOUR', defaultMessage: 'Circuit'})}</th>
                      <th>{intl.formatMessage({id: 'TOUR_RESERVATIONS.CLIENT', defaultMessage: 'Client'})}</th>
                      <th
                        role='button'
                        style={{cursor: 'pointer'}}
                        onClick={() => toggleSort('tourDate')}
                      >
                        {intl.formatMessage({id: 'TOUR_RESERVATIONS.TOUR_DATE'})}
                        {sortIndicator('tourDate')}
                      </th>
                      <th
                        role='button'
                        style={{cursor: 'pointer'}}
                        onClick={() => toggleSort('adultsCount')}
                      >
                        {intl.formatMessage({id: 'TOUR_RESERVATIONS.PARTICIPANTS'})}
                        {sortIndicator('adultsCount')}
                      </th>
                      <th
                        role='button'
                        style={{cursor: 'pointer'}}
                        onClick={() => toggleSort('totalPrice')}
                      >
                        {intl.formatMessage({id: 'TOUR_RESERVATIONS.TOTAL_PRICE'})}
                        {sortIndicator('totalPrice')}
                      </th>
                      <th>{intl.formatMessage({id: 'TOUR_RESERVATIONS.STATUS'})}</th>
                      <th>{intl.formatMessage({id: 'TOUR_RESERVATIONS.ACTIONS'})}</th>
                    </tr>
                  </thead>
                  <tbody className='text-gray-600 fw-semibold'>
                    {reservations.map((reservation: any) => (
                      <tr key={reservation._id}>
                        <td>{reservation.orderNumber}</td>
                        <td>{reservation.tour?.title || '—'}</td>
                        <td>{getClientName(reservation)}</td>
                        <td>{new Date(reservation.tourDate).toLocaleDateString()}</td>
                        <td>
                          {intl.formatMessage(
                            {id: 'TOUR_RESERVATIONS.PARTICIPANTS_COUNT'},
                            {
                              adults: reservation.adultsCount,
                              children: reservation.childrenCount,
                            }
                          )}
                        </td>
                        <td>{reservation.totalPrice.toFixed(2)}</td>
                        <td>
                          <span className='badge badge-light-info'>
                            {intl.formatMessage({
                              id: `STATUS.${normalizeReservationStatusCode(reservation.status?.code).toUpperCase()}`,
                            })}
                          </span>
                        </td>
                        <td>
                          <OverlayTrigger
                            placement='top'
                            overlay={
                              <Tooltip id={`tooltip-${reservation._id}`}>
                                {intl.formatMessage({id: 'ACTION.DETAILS'})}
                              </Tooltip>
                            }
                          >
                            <button
                              className='btn btn-icon btn-light-primary btn-sm me-1'
                              onClick={() =>
                                navigate(`/apps/tours/reservations/${reservation._id}`)
                              }
                            >
                              <KTSVG
                                path='/media/icons/duotune/art/art005.svg'
                                className='svg-icon-3'
                              />
                            </button>
                          </OverlayTrigger>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )
            })()
          )}
        </div>
        <div
          className='bo-pagination'
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            width: '100%',
            marginTop: 16,
            padding: '8px 16px 16px',
            flexWrap: 'wrap',
          }}
        >
          <button
            type='button'
            className='btn btn-light'
            disabled={filters.page === 0}
            onClick={() => setFilters({...filters, page: filters.page - 1})}
            style={{minWidth: 44, height: 40}}
          >
            ‹
          </button>
          <span style={{fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap'}}>
            {filters.page + 1}
          </span>
          <button
            type='button'
            className='btn btn-light'
            disabled={
              Array.isArray(data)
                ? data.length < filters.limit
                : Array.isArray(data?.data)
                ? data.data.length < filters.limit
                : true
            }
            onClick={() => setFilters({...filters, page: filters.page + 1})}
            style={{minWidth: 44, height: 40}}
          >
            ›
          </button>
          <select
            className='form-select'
            value={filters.limit}
            onChange={(e) => setFilters({...filters, limit: Number(e.target.value), page: 0})}
            style={{width: 88}}
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  )
}

export default TourReservationsPage
